import { TabPanel } from '@/components';
import { Space, Switch } from 'antd';
import { useTranslation } from 'react-i18next';
import { PersonalizationHistorySelect } from '../Personalization.Details/PersonalizationHistorySelect';
import {
  PersonalizationHistoryState,
  useGetProjectContextQuery,
} from '@/redux';
import { PersonalizationSummaryContent } from './PersonalizationSummaryContent';
import { ProjectContext } from '../Projects.Common';
import { useState } from 'react';
import Split from 'react-split';
import { HomeBaselinePanel } from '../HomeBaseline.Details/HomeBaselinePanel';
import styles from './PersonalizationSummary.module.scss';
import { useFullScreenAction } from '../Pricing.Details/useFullScreenAction';
import classNames from 'classnames';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

interface Props {
  homeId: string;
  personalizationHistory: PersonalizationHistoryState[];
  communityId: string;
}

export function PersonalizationSummaryOverviewTabContent(props: Props) {
  const { homeId, personalizationHistory, communityId } = props;
  const { t } = useTranslation();
  const [showCompare, setShowCompare] = useState(false);

  const [selectedProjectId, setSelectedProjectId] = useState<string>(
    personalizationHistory.at(0)?.projectId!,
  );

  const { data: projectContext } = useGetProjectContextQuery({
    id: selectedProjectId,
  });

  const { fullScreen, setFullScreen, fullScreenAction } = useFullScreenAction();

  if (!projectContext) {
    return null;
  }

  return (
    <div className={classNames({ [styles.fullScreen]: fullScreen })}>
      <TabPanel>
        <TabPanel.Title>{t('personalization.summary.title')}</TabPanel.Title>

        <TabPanel.Actions>
          <Space align="center">
            <Switch
              checked={showCompare}
              onChange={(value) => {
                setShowCompare(value);
                setFullScreen(value);
              }}
              checkedChildren={t('personalization.summary.compare')}
              unCheckedChildren={t('personalization.summary.current')}
            />
            {fullScreenAction}
          </Space>
        </TabPanel.Actions>
        <TabPanel.Body>
          {showCompare ? (
            <DndProvider backend={HTML5Backend}>
              <Split
                sizes={[50, 50]}
                minSize={510}
                gutterSize={5}
                className={styles.split}
              >
                <div style={{ overflow: 'auto', paddingRight: 8, minWidth: 0 }}>
                  <Space direction="vertical" size="middle" className="w-100">
                    <PersonalizationHistorySelect
                      onSelect={setSelectedProjectId}
                      selected={selectedProjectId}
                      value={personalizationHistory}
                      className={styles['min-80']}
                    />

                    <ProjectContext.Provider value={projectContext}>
                      <PersonalizationSummaryContent
                        communityId={communityId}
                        homeId={homeId}
                        projectId={selectedProjectId}
                        key={selectedProjectId}
                        skipProvider
                      />
                    </ProjectContext.Provider>
                  </Space>
                </div>
                <div style={{ overflow: 'auto', paddingLeft: 8 }}>
                  <Space direction="vertical" size="middle" className="w-100">
                    <div className={styles['min-80']}>
                      {t('homes.baseline.title')}
                    </div>

                    <HomeBaselinePanel
                      homeId={homeId}
                      filterClassName={styles['min-150']}
                      skipProvider
                    />
                  </Space>
                </div>
              </Split>
            </DndProvider>
          ) : (
            <Space direction="vertical" size="middle" className="w-100">
              <PersonalizationHistorySelect
                onSelect={setSelectedProjectId}
                selected={selectedProjectId}
                value={personalizationHistory}
              />

              <ProjectContext.Provider value={projectContext}>
                <PersonalizationSummaryContent
                  communityId={communityId}
                  homeId={homeId}
                  projectId={selectedProjectId}
                  key={selectedProjectId}
                />
              </ProjectContext.Provider>
            </Space>
          )}
        </TabPanel.Body>
      </TabPanel>
    </div>
  );
}
